import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { createLocation, createTrip, getAssignments, previewDistance } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';

export default function TripEntry() {
  const { vehicles, drivers, locations, refresh } = useApp();
  const [vehicle, setVehicle] = useState('');
  const [driver, setDriver] = useState('');
  const [date, setDate] = useState(() => toLocalISODate());
  const [outTime, setOutTime] = useState('');
  const [meterOut, setMeterOut] = useState('');
  const [purpose, setPurpose] = useState('');
  const [stops, setStops] = useState(['', '']);
  const [expectedKM, setExpectedKM] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [hasPO, setHasPO] = useState(false);
  const [poNumbers, setPoNumbers] = useState(['']);
  const [quickAdd, setQuickAdd] = useState({
    openFor: null,
    name: '',
    fullAddress: '',
    saving: false,
  });

  const stopOptions = useMemo(() => locations || [], [locations]);

  const loadAssignments = useCallback(async () => {
    if (!date) return;
    setLoadingAssignments(true);
    try {
      const res = await getAssignments(date);
      setAssignments(Array.isArray(res?.assignments) ? res.assignments : []);
      setActiveTrips(Array.isArray(res?.activeTrips) ? res.activeTrips : []);
    } catch {
      setAssignments([]);
      setActiveTrips([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, [date]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  /** vehicleId -> assigned driver */
  const assignedDriverByVehicle = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => {
      const vid = a.vehicle?._id || a.vehicle;
      const d = a.driver;
      if (vid && d) map.set(String(vid), d);
    });
    return map;
  }, [assignments]);

  /** driverId -> vehicle they are assigned to today */
  const assignedVehicleByDriver = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => {
      const did = a.driver?._id || a.driver;
      const v = a.vehicle;
      if (did && v) map.set(String(did), v);
    });
    return map;
  }, [assignments]);

  const activeByVehicle = useMemo(() => {
    const map = new Map();
    activeTrips.forEach((t) => {
      if (t.vehicleId) map.set(String(t.vehicleId), t);
    });
    return map;
  }, [activeTrips]);

  const activeByDriver = useMemo(() => {
    const map = new Map();
    activeTrips.forEach((t) => {
      if (t.driverId) map.set(String(t.driverId), t);
    });
    return map;
  }, [activeTrips]);

  const lockedDriver = vehicle ? assignedDriverByVehicle.get(String(vehicle)) : null;
  const vehicleOutTrip = vehicle ? activeByVehicle.get(String(vehicle)) : null;
  const effectiveDriverId = lockedDriver ? String(lockedDriver._id) : driver;
  const driverOutTrip = effectiveDriverId ? activeByDriver.get(String(effectiveDriverId)) : null;

  // Assigned driver is authoritative — keep the payload in sync with the lock.
  useEffect(() => {
    if (lockedDriver) {
      setDriver(String(lockedDriver._id));
    }
  }, [lockedDriver]);

  const onChangeVehicle = (value) => {
    setVehicle(value);
    const assigned = assignedDriverByVehicle.get(String(value));
    setDriver(assigned ? String(assigned._id) : '');
  };

  const updateStop = (idx, value) => {
    setStops((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const removeStop = (idx) => {
    setStops((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const openQuickAdd = (stopIndex) => {
    setQuickAdd({ openFor: stopIndex, name: '', fullAddress: '', saving: false });
  };

  const cancelQuickAdd = () => {
    setQuickAdd({ openFor: null, name: '', fullAddress: '', saving: false });
  };

  const handleQuickAddSave = async () => {
    const name = quickAdd.name.trim();
    const fullAddress = quickAdd.fullAddress.trim();
    if (name.length < 2) return toast.error('Location name needs at least 2 characters.');
    if (fullAddress.length < 5) return toast.error('Full address needs at least 5 characters.');

    setQuickAdd((q) => ({ ...q, saving: true }));
    try {
      const created = await createLocation({ name, fullAddress });
      const targetIndex = quickAdd.openFor;
      await refresh();
      if (created?._id != null && targetIndex != null) {
        updateStop(targetIndex, String(created._id));
      }
      toast.success(`Added "${created?.name || name}".`);
      setQuickAdd({ openFor: null, name: '', fullAddress: '', saving: false });
    } catch (err) {
      const status = err?.response?.status;
      const existing = err?.response?.data?.location;
      if (status === 409 && existing?._id) {
        const targetIndex = quickAdd.openFor;
        if (targetIndex != null) updateStop(targetIndex, String(existing._id));
        await refresh();
        toast(`That location already exists — selected "${existing.name}".`);
        setQuickAdd({ openFor: null, name: '', fullAddress: '', saving: false });
        return;
      }
      toast.error(err?.response?.data?.message || 'Failed to add location');
      setQuickAdd((q) => ({ ...q, saving: false }));
    }
  };

  const calcDistance = async () => {
    const ids = stops.filter(Boolean);
    if (ids.length < 2) {
      toast.error('Pick at least 2 stops');
      return;
    }
    const addresses = ids
      .map((id) => stopOptions.find((l) => String(l._id) === String(id))?.fullAddress)
      .filter(Boolean);
    if (addresses.length < 2) {
      toast.error('Selected stops need addresses');
      return;
    }
    try {
      const res = await previewDistance([...addresses, addresses[0]]);
      setExpectedKM(res.totalKM);
      toast.success(`Expected km: ${res.totalKM}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Distance failed');
    }
  };

  const blockedReason = (() => {
    if (vehicleOutTrip) {
      return `${vehicleOutTrip.vehicleName || 'This vehicle'} is already out with ${vehicleOutTrip.driverName || 'a driver'}. Record Vehicle In first.`;
    }
    if (driverOutTrip) {
      return `${driverOutTrip.driverName || 'This driver'} is already out on ${driverOutTrip.vehicleName || 'another vehicle'}. That vehicle must come in first.`;
    }
    if (vehicle && !lockedDriver && driver) {
      const promised = assignedVehicleByDriver.get(String(driver));
      if (promised && String(promised._id) !== String(vehicle)) {
        const driverName = drivers.find((d) => String(d._id) === String(driver))?.name || 'This driver';
        return `${driverName} is assigned to ${promised.name} today. Update Daily Assignments to move them.`;
      }
    }
    return null;
  })();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (blockedReason) {
      toast.error(blockedReason);
      return;
    }
    setSubmitting(true);
    try {
      await createTrip({
        vehicle,
        driver: effectiveDriverId,
        date,
        outTime,
        meterOut: Number(meterOut),
        purpose,
        stops: stops.filter(Boolean),
        hasPO,
        poNumbers: hasPO ? poNumbers.map((n) => n.trim()).filter(Boolean) : [],
      });
      toast.success('Trip out recorded');
      setMeterOut('');
      setOutTime('');
      setPurpose('');
      setExpectedKM(null);
      setStops(['', '']);
      setVehicle('');
      setDriver('');
      setHasPO(false);
      setPoNumbers(['']);
      await loadAssignments();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to save trip');
    } finally {
      setSubmitting(false);
    }
  };

  const sortedVehicles = useMemo(
    () => [...(vehicles || [])].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [vehicles]
  );

  return (
    <div>
      <div className="page-header">
        <h2>Vehicle Out</h2>
        <p>
          Pick the vehicle — today&apos;s assigned driver is filled in automatically and locked.
          A driver already out on a vehicle cannot take another until that one is back in.
        </p>
      </div>
      <form className="card" onSubmit={onSubmit}>
        <div className="form-grid cols2">
          <div className="form-group">
            <label>Vehicle</label>
            <select value={vehicle} onChange={(e) => onChangeVehicle(e.target.value)} required>
              <option value="">Select vehicle</option>
              {sortedVehicles.map((v) => {
                const out = activeByVehicle.get(String(v._id));
                const assigned = assignedDriverByVehicle.get(String(v._id));
                const suffix = out
                  ? ` — OUT with ${out.driverName || 'driver'}`
                  : (assigned ? ` — ${assigned.name}` : ' — not assigned');
                return (
                  <option key={v._id} value={v._id} disabled={Boolean(out)}>
                    {v.name} ({v.plate}){suffix}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label>Driver {lockedDriver ? '(assigned — locked)' : ''}</label>
            {lockedDriver ? (
              <input value={lockedDriver.name} readOnly disabled />
            ) : (
              <select
                value={driver}
                onChange={(e) => setDriver(e.target.value)}
                required
                disabled={!vehicle}
              >
                <option value="">
                  {vehicle ? 'Select driver' : 'Select a vehicle first'}
                </option>
                {drivers.map((d) => {
                  const out = activeByDriver.get(String(d._id));
                  const promised = assignedVehicleByDriver.get(String(d._id));
                  const busyElsewhere = promised && String(promised._id) !== String(vehicle);
                  const suffix = out
                    ? ` — out on ${out.vehicleName || 'vehicle'}`
                    : (busyElsewhere ? ` — assigned to ${promised.name}` : '');
                  return (
                    <option
                      key={d._id}
                      value={d._id}
                      disabled={Boolean(out) || Boolean(busyElsewhere)}
                    >
                      {d.name}{suffix}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Out time</label>
            <input value={outTime} onChange={(e) => setOutTime(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Meter out</label>
            <input type="number" value={meterOut} onChange={(e) => setMeterOut(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Purpose</label>
            <input value={purpose} onChange={(e) => setPurpose(e.target.value)} />
          </div>
        </div>

        {vehicle && !lockedDriver && !loadingAssignments && (
          <p className="text-muted text-sm mt8">
            No driver assigned to this vehicle today.{' '}
            <Link to="/assignments">Set Daily Assignments</Link> to lock the pairing.
          </p>
        )}

        {blockedReason && (
          <div className="alert-banner suspicious mt12">
            <span>⚠</span>
            <span>{blockedReason}</span>
          </div>
        )}

        <div className="mt16">
          <div className="card-title">Route stops</div>
          {stops.map((stop, idx) => (
            <div className="form-group mb12" key={idx}>
              <label>Stop {idx + 1}</label>
              <div className="form-row-flex">
                <select value={stop} onChange={(e) => updateStop(idx, e.target.value)}>
                  <option value="">Select location</option>
                  {stopOptions.map((l) => (
                    <option key={l._id} value={l._id}>{l.name}</option>
                  ))}
                </select>
                <button type="button" className="btn btn-sm" onClick={() => openQuickAdd(idx)}>
                  + New
                </button>
                {stops.length > 2 && (
                  <button type="button" className="btn btn-sm" onClick={() => removeStop(idx)}>
                    ✕
                  </button>
                )}
              </div>
              {quickAdd.openFor === idx && (
                <div className="card card-compact">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      value={quickAdd.name}
                      onChange={(e) => setQuickAdd((q) => ({ ...q, name: e.target.value }))}
                      placeholder="e.g. DHA Phase 5"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>Full address</label>
                    <input
                      value={quickAdd.fullAddress}
                      onChange={(e) => setQuickAdd((q) => ({ ...q, fullAddress: e.target.value }))}
                      placeholder="Full street address for Google Maps"
                    />
                  </div>
                  <div className="btn-row">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleQuickAddSave}
                      disabled={quickAdd.saving}
                    >
                      {quickAdd.saving ? 'Saving…' : 'Save location'}
                    </button>
                    <button type="button" className="btn btn-sm" onClick={cancelQuickAdd}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={() => setStops([...stops, ''])}>
            Add stop
          </button>
          <button type="button" className="btn btn-sm ml8" onClick={calcDistance}>
            Preview distance
          </button>
          {expectedKM != null && (
            <span className="text-muted ml8">Expected: {expectedKM} km</span>
          )}
        </div>

        <div className="po-section mt16">
          <label className="po-toggle-label">
            <span className="po-toggle-checkbox">
              <input
                type="checkbox"
                checked={hasPO}
                onChange={(e) => {
                  setHasPO(e.target.checked);
                  if (e.target.checked && poNumbers.length === 0) setPoNumbers(['']);
                }}
              />
              <span className="po-checkbox-visual" />
            </span>
            <span className="po-toggle-icon">📄</span>
            <span className="po-toggle-text">
              <strong>Trip has PO / Challan number</strong>
              <small>Check this if the trip is linked to a purchase order or challan</small>
            </span>
          </label>

          {hasPO && (
            <div className="po-inputs-panel">
              <div className="po-inputs-header">
                <span className="po-inputs-title">PO / Challan Numbers</span>
                <span className="po-inputs-hint">You can add multiple</span>
              </div>
              {poNumbers.map((num, idx) => (
                <div className="po-input-row" key={idx}>
                  <span className="po-input-num">{idx + 1}</span>
                  <input
                    className="po-input"
                    value={num}
                    onChange={(e) => setPoNumbers((prev) => prev.map((v, i) => (i === idx ? e.target.value : v)))}
                    placeholder={`PO / Challan No. ${idx + 1}`}
                  />
                  {poNumbers.length > 1 && (
                    <button
                      type="button"
                      className="po-remove-btn"
                      title="Remove"
                      onClick={() => setPoNumbers((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm po-add-btn"
                onClick={() => setPoNumbers((prev) => [...prev, ''])}
              >
                + Add another
              </button>
            </div>
          )}
        </div>

        <button className="btn btn-primary mt16" disabled={submitting || Boolean(blockedReason)}>
          {submitting ? 'Saving…' : 'Record trip out'}
        </button>
      </form>
    </div>
  );
}
